/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

class CalendlyFormHandler {
  constructor() {
    this.forms = [...document.querySelectorAll("[calendly-form-type]")];
    this.url = this._getUrls();
  }

  _init() {
    this.forms.forEach((form) => {
      form.addEventListener("submit", (e) => {
        this._showApptForm(e, form);
      });
    });
  }

  _createColorParam() {
    const colors = {
      background_color: "fdfcf8",
      text_color: "313131",
      primary_color: "d1741b",
    };

    return Object.entries(colors)
      .map(([key, value]) => `${key}=${value}`)
      .join("&");
  }

  _showApptForm(event, form) {
    event.preventDefault();
    const formType = form.getAttribute("calendly-form-type");
    const apptType = form.querySelector("[name='appt-type']").value;
    const apptTime = form.querySelector("[name='appt-time']").value;
    const formUrl = this.url[formType]?.[apptType]?.[apptTime] || null;

    console.log("Form type: " + formType);
    console.log("Appt type: " + apptType);
    console.log("Appt time: " + apptTime);
    console.log("Form url: " + formUrl);

    if (formUrl) {
      Calendly.initPopupWidget({
        url: `${formUrl}?${this._createColorParam()}`,
        resize: true,
      });
    } else {
      alert("Please select a valid meeting type and duration.");
    }
  }

  _getUrls() {
    return {
      "in-person": {
        collections: {
          30: "https://calendly.com/d/crhk-8wk-7fk/srj-collections-consultation-in-person",
          60: "https://calendly.com/d/cmzn-w9r-th3/srj-collections-consultation-in-person",
        },
        bridal: {
          30: "https://calendly.com/d/cm2m-jcz-q7v/bridal-engagement-consultation-in-person",
          60: "https://calendly.com/d/cnk6-p3g-x3s/bridal-engagement-consultation-in-person",
        },
        resetting: {
          30: "https://calendly.com/d/cnk4-gcw-2jq/resetting-consultation-in-person",
          60: "https://calendly.com/d/cnqm-3bk-rwk/resetting-consultation-in-person",
        },
        custom: {
          30: "https://calendly.com/d/crpp-9h8-txv/custom-design-consultation-in-person",
          60: "https://calendly.com/d/crpc-tcm-xcf/custom-design-consultation-in-person",
        },
      },
      virtual: {
        collections: {
          30: "https://calendly.com/d/cnr6-3fr-t66/srj-collections-consultation-virtual-30min",
          60: "https://calendly.com/d/cm2k-yct-z76/srj-collections-consultation-virtual-1hr",
        },
        bridal: {
          30: "https://calendly.com/d/cms5-6hf-866/bridal-engagement-consultation-virtual",
          60: "https://calendly.com/d/cqr7-85q-6ry/bridal-engagement-consultation-virtual",
        },
        resetting: {
          30: "https://calendly.com/d/cqqq-2qt-gnc/resetting-consultation-virtual",
          60: "https://calendly.com/d/cqwt-hpf-82n/resetting-consultation-virtual",
        },
        custom: {
          30: "https://calendly.com/d/cqqx-2cb-ykx/custom-design-consultation-virtual",
          60: "https://calendly.com/d/cqr9-2pp-c4z/custom-design-consultation-virtual",
        },
      },
    };
  }
}

class ThankYouPageHandler {
  constructor() {
    this.eventName =
      this.getQueryParam("event_type_name") || "your appointment";
    this.eventDate = this.getQueryParam("event_start_time");
    this.inviteeName = this.getQueryParam("invitee_first_name") || "Guest";
    this.formattedDate = this.eventDate
      ? new Date(this.eventDate).toLocaleString()
      : "";
  }

  getQueryParam(param) {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(param);
  }

  displayMessage() {
    document.getElementById("thank-you-message").innerText =
      `Thanks for booking, ${this.inviteeName}! Your ${this.eventName} is scheduled for ${this.formattedDate}.`;
  }

  init() {
    this.displayMessage();
  }
}

const customGalImgHandler = {
  imgWrappers: [...document.querySelectorAll(".custom_gallery--image_wrapper")],
  init() {
    this.imgWrappers.forEach((wrapper) => {
      const primaryImg = wrapper.querySelector(
          ".custom_gallery--primary_image"
        ),
        secondaryImg = wrapper.querySelector(
          ".custom_gallery--secondary_image"
        );

      const toggleImages = () => {
        primaryImg.classList.toggle("hidden");
        secondaryImg.classList.toggle("hidden");
      };

      if (secondaryImg) {
        wrapper.addEventListener("mouseover", toggleImages);
        wrapper.addEventListener("mouseout", toggleImages);
      }
    });
  },
};

const dynamicPreOrderDate = {
  preOrderWrapper: document.querySelector(".pre-order--message"),
  calcShipDate(date, numDays) {
    const newDate = new Date(date);
    newDate.setDate(newDate.getDate() + numDays);
    return newDate;
  },
  updateDate() {
    if (!this.preOrderWrapper) return;
    const timeline = Number(this.preOrderWrapper.dataset.timeline) || 7;
    const shipDate = this.calcShipDate(new Date(), timeline);

    const dateWrapper = this.preOrderWrapper.querySelector("#dynamic-date");
    dateWrapper.textContent = shipDate.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  },
  init() {
    this.updateDate();
  },
};

document.addEventListener("DOMContentLoaded", function () {
  const apptConfirmPath = "/pages/appointment-confirmation";

  if (window.location.pathname.includes(apptConfirmPath)) {
    const thankYouHandler = new ThankYouPageHandler();
    thankYouHandler.init();
  }

  const calendly = new CalendlyFormHandler();
  calendly._init();

  customGalImgHandler.init();
  dynamicPreOrderDate.init();
});
